/**
 * The app bar's Help control — where a training video stays reachable after the
 * dashboard tile has been dismissed.
 *
 * Module-aware: the current module's own video is listed first and called out as
 * such, then the rest of the library, then a link to the full public library.
 * Every member of a module gets it — a training video carries no admin gate, and
 * the menu keys off whichever module is currently resolved.
 *
 * Two variants, like `AppBarMailFilesShortcuts`: `inline` is the desktop icon
 * button, `menu` is one flat row inside the mobile overflow menu. Unlike that
 * component this one NEVER returns null — Help is chrome a user goes looking
 * for, so it is present even on the ~40 modules with no cut yet, where it offers
 * the library and the other modules' videos instead of an empty menu.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp, ExternalLink, PlayCircle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useModule } from '../../shared/contexts/ModuleContext';
import {
  allTrainingVideos,
  libraryUrl,
  resolveCut,
  TrainingVideoDialog,
  type TrainingVideo,
} from '../../shared/training-videos';

interface AppBarHelpMenuProps {
  /**
   * A CSS color string for the module accent — pass `MODULE_ACCENT_COLOR`
   * (`shared/utils/color`), never a raw module hex, which goes unreadable in one
   * of the two themes.
   */
  moduleColor: string;
  variant?: 'inline' | 'menu';
  /** Called after an item is activated, so the mobile overflow menu can close. */
  onNavigate?: () => void;
}

export function AppBarHelpMenu({
  moduleColor,
  variant = 'inline',
  onNavigate,
}: AppBarHelpMenuProps) {
  const { t, i18n } = useTranslation('common');
  const { currentModule } = useModule();
  const [playing, setPlaying] = useState<TrainingVideo | null>(null);

  const videos = allTrainingVideos();
  const thisModule = videos.find((video) => video.module === currentModule.id);
  const others = videos.filter((video) => video !== thisModule);

  const label = t('help.menu.label');

  const play = (video: TrainingVideo) => {
    setPlaying(video);
    onNavigate?.();
  };

  const openLibrary = () => {
    window.open(libraryUrl(), '_blank', 'noopener,noreferrer');
    onNavigate?.();
  };

  const videoRow = (video: TrainingVideo) => {
    const cut = resolveCut(video, i18n.language);
    const minutes = cut ? Math.max(1, Math.round(cut.durationSeconds / 60)) : null;
    return (
      <DropdownMenuItem key={video.slug} onClick={() => play(video)}>
        <PlayCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="ms-2 flex-1 text-start">{t(video.titleKey)}</span>
        {minutes !== null && (
          <span className="ms-2 text-xs text-muted-foreground">
            {t('help.menu.minutes', { minutes })}
          </span>
        )}
      </DropdownMenuItem>
    );
  };

  const menuContent = (
    <DropdownMenuContent align="end" className="min-w-[260px]">
      {thisModule && (
        <>
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {t('help.menu.thisModule')}
          </DropdownMenuLabel>
          {videoRow(thisModule)}
          {others.length > 0 && <DropdownMenuSeparator />}
        </>
      )}
      {others.length > 0 && (
        <>
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {t('help.menu.otherModules')}
          </DropdownMenuLabel>
          {others.map(videoRow)}
        </>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={openLibrary}>
        <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="ms-2">{t('help.menu.browseLibrary')}</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  const player = playing && (
    <TrainingVideoDialog
      video={playing}
      open
      onOpenChange={(open) => {
        if (!open) setPlaying(null);
      }}
    />
  );

  if (variant === 'menu') {
    // Flat rows, never a nested dropdown: that is the house pattern for this
    // menu ("a flat list of every present item, always direct" —
    // AppBarMailFilesShortcuts), and Radix does not nest a DropdownMenu inside a
    // DropdownMenuContent cleanly. Ordered so the current module's video is the
    // first thing under the Help heading.
    const rows = thisModule ? [thisModule, ...others] : others;
    return (
      <>
        <div className="px-2 pt-1 pb-0.5 text-xs text-muted-foreground">{label}</div>
        {rows.map((video) => (
          <Button
            key={video.slug}
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 font-normal text-foreground"
            onClick={() => play(video)}
            data-testid={`app-bar-help-video-${video.slug}`}
          >
            <PlayCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 text-start">{t(video.titleKey)}</span>
          </Button>
        ))}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 font-normal text-foreground"
          onClick={openLibrary}
          data-testid="app-bar-help-menu-item"
        >
          <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 text-start">{t('help.menu.browseLibrary')}</span>
        </Button>
        {player}
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-accent"
                style={{ color: moduleColor }}
                aria-label={label}
                data-testid="app-bar-help-menu"
              >
                <CircleHelp className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
        {menuContent}
      </DropdownMenu>
      {player}
    </>
  );
}
